import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Plus, SlidersHorizontal } from 'lucide-react'
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { parseAsString, useQueryState } from 'nuqs'

import useLocale from '@/hooks/use-locale'

import { BlogSkeleton } from '@/components/cards/blog-card'
import { HiringCard } from '@/components/cards/hiring-card'
import { PageHeader, PageHeaderDescription, PageHeaderHeading } from '@/components/common/page-header'
import { Shell } from '@/components/common/shell'
import ViewTracker from '@/components/common/view-tracker'
import PaginationComponent from '@/components/PaginationComponent'
import SEO from '@/components/seo/SEO'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import RootContainer from '@/containers/root-container'

import { useAuth } from '@/contexts/auth-context'
import hiringService from '@/services/hiring.service'

import { HiringWithCompany } from './[slug]'

import { ListResponse } from '@/types/list.type'

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const { res } = context
  res?.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59')
  const query = context.query
  const hiringList = await hiringService.getPublicHirings<ListResponse<HiringWithCompany>>({
    pageNumber: Number(query.page) || 1,
    pageSize: Number(query.perPage) || 10,
  })

  return {
    props: { hiringList },
  }
}

export default function HiringJobList({
  hiringList: initHiringList,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const searchParams = useSearchParams()
  const pageIndex = Number(searchParams.get('page')) || 1
  const [sortHiringBy, setSortHiringBy] = useQueryState('sortHiringBy', parseAsString.withDefault('-createdAt'))

  const { lang } = useLocale()

  const { user } = useAuth()

  const { data: hiringList, isFetching } = useQuery({
    queryKey: ['hirings', pageIndex, sortHiringBy],
    queryFn: () =>
      hiringService.getPublicHirings<ListResponse<HiringWithCompany>>({
        pageNumber: pageIndex,
        pageSize: 10,
        sort: [sortHiringBy],
      }),
    initialData: initHiringList,
    refetchOnWindowFocus: false,
  })
  return (
    <RootContainer>
      <SEO
        title="FSDS Jobs"
        description="FSDS luôn cập nhật những tin tức, xu hướng công nghệ mới nhất, kiến thức chuyên sâu và các bài giảng chất lượng."
        ogImage="/images/og-job.jpg"
        ogType="website"
        twitterCard="summary_large_image"
        canonicalUrl="https://fullstackdatascience.com/jobs"
      />
      <ViewTracker entityType="page" entityId="/jobs" />
      <JobsHeader />

      <div className="container">
        <JobSorting sortHiringBy={sortHiringBy} setSortHiringBy={setSortHiringBy} />

        <div className="py-6">
          {isFetching ? (
            <BlogSkeleton />
          ) : (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                {hiringList.data.map(job => (
                  <HiringCard key={job.id} job={job} user={user} lang={lang} />
                ))}
              </div>
            </motion.div>
          )}
        </div>

        <div className="mt-8 py-6 md:py-10">
          <PaginationComponent currentPage={pageIndex} pageSize={10} itemCount={hiringList?.meta?.totalItems || 10} />
        </div>
      </div>
    </RootContainer>
  )
}

type JobSortingProps = {
  sortHiringBy: string
  setSortHiringBy: (value: string) => void
}

function JobSorting({ sortHiringBy, setSortHiringBy }: JobSortingProps) {
  const { isAuthenticated } = useAuth()

  return (
    <div className="flex sm:flex-row flex-col justify-start items-center gap-2 mb-8">
      <motion.div
        whileTap={{ scale: 0.97 }}
        whileHover={{ scale: 1.03 }}
        transition={{ type: 'spring', stiffness: 300 }}
        className="w-full sm:w-auto"
      >
        <Button
          asChild
          variant="default"
          className="w-full sm:w-auto justify-center px-5 py-3 text-base sm:text-sm font-semibold from-red-600 to-pink-500 hover:from-red-500 hover:to-pink-400 text-white transition-all duration-200 shadow-md sm:shadow-lg"
        >
          <Link href={isAuthenticated ? '/dashboard/jobs' : '/sign-in'} className="flex items-center gap-2">
            <Plus className="h-5 w-5 sm:h-4 sm:w-4" />
            Post a Job
          </Link>
        </Button>
      </motion.div>
      <Select onValueChange={setSortHiringBy} value={sortHiringBy}>
        <SelectTrigger className="w-full sm:w-[180px] border-blue-200 bg-white hover:border-blue-300 transition-colors duration-300">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-3.5 w-3.5 text-blue-600" />
            <SelectValue placeholder="Sort by" />
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="-createdAt">Newest first</SelectItem>
          <SelectItem value="createdAt">Oldest first</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}

function JobsHeader() {
  return (
    <Shell className="gap-0 md:pb-3 lg:pb-10">
      <PageHeader
        as="section"
        className="relative z-10 mx-auto flex flex-col items-center gap-4 py-10 text-center md:py-14 lg:py-16"
      >
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col items-center"
        >
          <PageHeaderHeading className="text-3xl md:text-5xl lg:text-6xl font-bold bg-clip-text">
            Discover Your Dream Job
          </PageHeaderHeading>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <PageHeaderDescription className="max-w-3xl text-lg md:text-xl text-muted-foreground">
            Explore exciting career opportunities with leading companies in the tech industry. Find the perfect match
            for your skills and aspirations.
          </PageHeaderDescription>
        </motion.div>
      </PageHeader>
    </Shell>
  )
}
